import { z } from 'zod';
import { isoDateTimeSchema, sha256HexSchema, uuidSchema } from './common.js';
import { libraryTypeEnum, tierAccessEnum } from './track.js';
/**
 * Upload pipeline DTOs (admin-arch §5.1 / PRD §3.5).
 *
 * Two upload kinds:
 *   - `single`     — one .mp3 master.
 *   - `zip_batch`  — ZIP containing many .mp3 files (stream-extracted in
 *     worker pipeline, soft-fails per-entry into an errors.csv).
 *
 * `master_replace` (admin-arch §2.4) is a separate flow and intentionally
 * NOT exposed via these schemas — it ships with the catalog edit endpoints
 * later in the wave.
 */
export const uploadTypeSchema = z.enum(['single', 'zip_batch']);
/**
 * tus `upload_url` returned by the init/replace endpoints. It is a HOST-LESS
 * relative path (`/admin/upload/tus/<hex>`) so the upload stays same-origin
 * wherever the admin SPA is served (BUG-126); the FE resolves it against its
 * own origin before handing it to tus-js-client. `z.string().url()` rejects a
 * relative path ("Invalid url"), which broke upload init after BUG-126
 * (BUG-130) — accept a root-relative path OR an absolute http(s) URL.
 */
const tusUploadUrlSchema = z
    .string()
    .min(1)
    .refine((v) => v.startsWith('/') || /^https?:\/\//.test(v), {
    message: 'Invalid url',
});
export const uploadMimeTypeSchema = z.enum(['audio/mpeg', 'application/zip']);
/**
 * `POST /admin/upload/init` request body.
 *
 * Size hard ceiling is 5 GiB — the value is also recomputed against
 * `MAX_UPLOAD_SIZE_MB` (per-file, single) inside the service so the env can
 * tighten the limit without a schema bump. The service refuses
 * `upload_type = 'zip_batch'` (BUG-402); the type stays valid for stored rows.
 */
export const uploadInitSchema = z.object({
    filename: z.string().min(1).max(500),
    size_bytes: z
        .number()
        .int()
        .positive()
        .max(5 * 1024 * 1024 * 1024),
    mime_type: uploadMimeTypeSchema,
    upload_type: uploadTypeSchema,
    /**
     * BUG-071 / BUG-077 (upload-page part) — operator chooses the target
     * library + minimum tariff at upload time, applied to every track the
     * session produces. Both are optional for backwards compatibility: when
     * absent the API falls back to curated / lite. A selector's
     * `library_type` is honoured like staff's; their `tier_access` is ignored —
     * a selector's upload always gets standard, the tier stays the admin's
     * (CEO 2026-10-29, STORY-103).
     */
    library_type: libraryTypeEnum.optional(),
    tier_access: tierAccessEnum.optional(),
    /**
     * BUG-149 — operator-chosen tags applied to every track the session
     * produces. Tag UUIDs come from the dictionary (`GET /admin/tags`). The
     * service drops ids that don't resolve to a live (non-deleted) tag, so a
     * stale picker can't 500 the upload. Capped to keep the upload row small.
     */
    tags: z.array(uuidSchema).max(50).optional(),
});
/**
 * `POST /admin/upload/init` response.
 *
 * `upload_id` — 32 lowercase hex chars (16 random bytes). Distinct from the
 * server-side UUIDv7 row PK so that even if the row id leaks via audit log,
 * the active multipart URL stays unguessable.
 */
export const uploadInitResponseSchema = z.object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    upload_url: tusUploadUrlSchema,
    expires_at: isoDateTimeSchema,
});
/**
 * tus chunk write — used by the API for diagnostics / tests. Actual wire
 * format is tus protocol headers (Upload-Offset, Upload-Length, Tus-Resumable).
 */
export const uploadChunkSchema = z.object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    offset: z.number().int().nonnegative(),
});
/**
 * `POST /admin/upload/:upload_id/complete` response.
 *
 * `status='completed'` — single track ready for transcoding.
 * `status='requires_dup_review'` — binary duplicate (audio_sha256 collides
 * with an existing live row). The duplicate path is admin-arch §5.5 (soft
 * dup workflow); selectors resolve via the catalog UI.
 *
 * For zip_batch, `batch_summary` is populated; for single, `track_id` is.
 */
export const batchUploadSummarySchema = z.object({
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    errors_csv_url: z.string().url().optional(),
});
export const uploadCompleteResponseSchema = z.object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    status: z.enum(['completed', 'requires_dup_review']),
    audio_sha256: sha256HexSchema.optional(),
    track_id: uuidSchema.optional(),
    batch_summary: batchUploadSummarySchema.optional(),
});
/**
 * Live batch status — polled by the admin SPA via REST until websocket
 * progress hooks land in a later wave.
 */
export const batchUploadStatusSchema = z.object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    status: z.enum(['in_progress', 'completed', 'aborted']),
    bytes_received: z.number().int().nonnegative(),
    size_bytes: z.number().int().nonnegative(),
    summary: batchUploadSummarySchema.nullable(),
});
/**
 * Replace-master upload kind (Wave 2.18 BE-2, TASK-043a). Distinct from
 * `single`/`zip_batch` so the tus finish hook routes it to the version-
 * insertion path instead of the new-track path.
 *
 * Max size 300 MiB — matches the single-file ceiling (BUG-101). Content types
 * narrow to the formats the worker pipeline can actually transcode.
 */
export const replaceMasterContentTypeSchema = z.enum([
    'audio/wav',
    'audio/flac',
    'audio/mpeg',
]);
const REPLACE_MASTER_MAX_BYTES = 300 * 1024 * 1024;
export const replaceMasterRequestSchema = z
    .object({
    filename: z.string().min(1).max(500),
    filesize: z.number().int().positive().max(REPLACE_MASTER_MAX_BYTES),
    content_type: replaceMasterContentTypeSchema,
    upload_kind: z.literal('master_replace'),
})
    .strict();
/**
 * Response from `POST /admin/tracks/:id/replace-master`. Re-uses the tus
 * coordinates produced by the upload pipeline so the FE drives the same
 * `@tus/server` surface as new uploads.
 *
 * `version_number` is reserved at init time so the FE can display the
 * upcoming version label before transcoding finishes. The actual
 * `track_versions` row is inserted by the tus finish hook.
 */
export const replaceMasterResponseSchema = z
    .object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    upload_url: tusUploadUrlSchema,
    upload_token: z.string().regex(/^[a-f0-9]{32}$/),
    expires_at: isoDateTimeSchema,
    reserved_version_number: z.number().int().positive(),
})
    .strict();
/**
 * Batch duplicate review (Wave 2.40 BE-1, TASK-221).
 *
 * After a ZIP upload finishes the FE polls
 * `GET /admin/uploads/batch/:batch_id/results` for the per-file outcome:
 *
 *   - `created`   — track row exists, transcoding queued. Nothing to do.
 *   - `duplicate` — audio fingerprint collided with an existing live track;
 *                   admin must pick a resolution. `duplicate_of_track_id`
 *                   plus the matched title/artist are surfaced for UX.
 *   - `error`     — extractor rejected the entry (unsafe path, oversize,
 *                   mime mismatch). `error` carries the human-readable reason.
 *
 * `batch_id` is the parent zip-batch `upload_id_hex` (32 hex); individual
 * `upload_id`s are the child uploads.
 */
export const uploadBatchItemStatusSchema = z.enum(['created', 'duplicate', 'error']);
export const uploadBatchResultItemSchema = z
    .object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    file_name: z.string(),
    status: uploadBatchItemStatusSchema,
    duplicate_of_track_id: uuidSchema.nullable().optional(),
    duplicate_of_title: z.string().nullable().optional(),
    duplicate_of_artist: z.string().nullable().optional(),
    duplicate_of_duration_seconds: z.number().int().nonnegative().nullable().optional(),
    error: z.string().nullable().optional(),
})
    .strict();
export const uploadBatchResultsResponseSchema = z
    .object({
    items: z.array(uploadBatchResultItemSchema),
})
    .strict();
/**
 * Per-upload resolution choice the admin makes after reviewing duplicates.
 *
 *   - `replace` — discard the matching live master and ingest the new upload
 *                 as a fresh version on the SAME `duplicate_of_track_id`.
 *                 Preserves the catalog row id and all venue uses.
 *   - `new`     — override the duplicate check: create a brand-new track row
 *                 from the upload. Used for legitimate alternate cuts /
 *                 remasters that share the audio fingerprint.
 *   - `skip`    — discard the upload. The orphan-uploads cron removes the
 *                 pending S3 object eventually.
 */
export const uploadResolutionActionSchema = z.enum(['replace', 'new', 'skip']);
export const uploadResolutionItemSchema = z
    .object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    action: uploadResolutionActionSchema,
    /**
     * Optional client-side hint for `replace` — when present the server cross-
     * checks it against the recorded `summary.duplicate_of_track_id` and 422s
     * on mismatch. Defends against stale dialog state pointing the replace at
     * a different track than the operator saw.
     */
    duplicate_of_track_id: uuidSchema.optional(),
})
    .strict();
export const uploadResolveDuplicatesRequestSchema = z
    .object({
    resolutions: z.array(uploadResolutionItemSchema).min(1).max(200),
})
    .strict();
export const uploadResolutionOutcomeSchema = z
    .object({
    upload_id: z.string().regex(/^[a-f0-9]{32}$/),
    action: uploadResolutionActionSchema,
    track_id: uuidSchema.nullable(),
    /** Set for `replace` only — the new version row that was inserted. */
    version_id: uuidSchema.optional(),
    version_number: z.number().int().positive().optional(),
})
    .strict();
export const uploadResolveDuplicatesResponseSchema = z
    .object({
    applied: z.number().int().nonnegative(),
    outcomes: z.array(uploadResolutionOutcomeSchema),
})
    .strict();
//# sourceMappingURL=upload.js.map